import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Searcher,
  useHistory,
  historyPush,
  useModulesManager,
  useTranslations,
} from '@openimis/fe-core';
import { IconButton, Tooltip, Chip, Box } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CancelIcon from '@material-ui/icons/Cancel';
import {
  RIGHT_TASKS_MANAGEMENT_SEARCH, DEFAULT_PAGE_SIZE, ROWS_PER_PAGE_OPTIONS, TASK_STATUS, TASK_ROUTE,
} from '../constants';
import TaskFilter from './TaskFilter';
import { fetchTasks } from '../actions';
import trimBusinessEvent from '../utils/trimBusinessEvent';

function TaskSearcher({
  rights, contribution, entityId, entityIds, showFilters = true,
}) {
  const history = useHistory();
  const modulesManager = useModulesManager();
  const dispatch = useDispatch();
  const {
    formatMessage,
    formatMessageWithValues,
    formatDateTimeFromISO,
  } = useTranslations('tasksManagement', modulesManager);

  const fetchingTasks = useSelector((state) => state?.tasksManagement?.fetchingTasks);
  const fetchedTasks = useSelector((state) => state?.tasksManagement?.fetchedTasks);
  const errorTasks = useSelector((state) => state?.tasksManagement?.errorTasks);
  const tasks = useSelector((state) => state?.tasksManagement?.tasks);
  const tasksPageInfo = useSelector((state) => state?.tasksManagement?.tasksPageInfo);
  const tasksTotalCount = useSelector((state) => state?.tasksManagement?.tasksTotalCount);

  const openTask = (task, newTab = false) => historyPush(
    modulesManager,
    history,
    TASK_ROUTE,
    [task?.id],
    newTab,
  );

  const onDoubleClick = (task) => openTask(task);
  const fetch = (params) => dispatch(fetchTasks(modulesManager, params));

  const rowIdentifier = (task) => task.id;

  const isRowDisabled = (_, task) => task.status !== TASK_STATUS.ACCEPTED;

  const headers = () => {
    const headers = [
      'task.source',
      'task.type',
      'task.entity',
      'task.assignee',
      'task.executors',
      'task.dateCreated',
      'task.status',
    ];
    if (rights.includes(RIGHT_TASKS_MANAGEMENT_SEARCH)) {
      headers.push('emptyLabel');
    }
    return headers;
  };

  const sorts = () => [
    ['source', true],
    ['type', true],
    ['entity', true],
    ['assignee', true],
    ['date_created', true],
    ['status', true],
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircleIcon style={{ fontSize: 16, color: '#4caf50' }} />;
      case 'PENDING':
        return <HourglassEmptyIcon style={{ fontSize: 16, color: '#ff9800' }} />;
      case 'REJECTED':
      case 'FAILED':
        return <CancelIcon style={{ fontSize: 16, color: '#f44336' }} />;
      default:
        return null;
    }
  };

  const formatExecutors = (task) => {
    const executors = task?.executorsStatus || [];
    
    if (executors.length === 0) {
      return '-';
    }

    return (
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {executors.map((executor, index) => (
          <Tooltip 
            key={index} 
            title={`${executor.fullName} - ${executor.statusDisplay}`}
          >
            <Chip
              size="small"
              icon={getStatusIcon(executor.status)}
              label={executor.username}
              variant="outlined"
              style={{ 
                margin: 2,
                backgroundColor: 
                  executor.status === 'APPROVED' ? '#e8f5e9' :
                  executor.status === 'PENDING' ? '#fff3e0' :
                  executor.status === 'REJECTED' || executor.status === 'FAILED' ? '#ffebee' :
                  'transparent'
              }}
            />
          </Tooltip>
        ))}
      </Box>
    );
  };

  const itemFormatters = () => [
    (task) => task?.sourceLabel || task?.source,
    (task) => task?.typeLabel || trimBusinessEvent(task?.businessEvent),
    (task) => task?.entityLabel || task?.entityString,
    (task) => task?.taskGroup?.code,
    (task) => formatExecutors(task),
    (task) => formatDateTimeFromISO(task?.dateCreated),
    (task) => task.status,
    (task) => (
      <Tooltip title={formatMessage('viewDetailsButton.tooltip')}>
        <IconButton
          onClick={() => openTask(task)}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
    ),
  ];

  const defaultFilters = () => {
    const filters = {
      isDeleted: {
        value: false,
        filter: 'isDeleted: false',
      },
    };
    if (contribution?.taskSource) {
      filters.source = {
        value: contribution.taskSource,
        filter: `source: "${contribution.taskSource}"`,
      };
    }
    if (entityId) {
      filters.entityId = {
        value: entityId,
        filter: `entityId: "${entityId}"`,
      };
    }
    if (entityIds) {
      filters.entityIds = {
        value: entityIds,
        filter: `entityIds: [${entityIds.map((id) => `"${id}"`)}]`,
      };
    }
    return filters;
  };

  const taskFilter = (props) => (
    <TaskFilter
      intl={props.intl}
      classes={props.classes}
      filters={props.filters}
      onChangeFilters={props.onChangeFilters}
    />
  );

  return (
    <Searcher
      module="tasksManagement"
      FilterPane={showFilters && taskFilter}
      fetch={fetch}
      items={tasks}
      itemsPageInfo={tasksPageInfo}
      fetchingItems={fetchingTasks}
      fetchedItems={fetchedTasks}
      errorItems={errorTasks}
      tableTitle={formatMessageWithValues('task.searcherResultsTitle', {
        tasksTotalCount,
      })}
      headers={headers}
      itemFormatters={itemFormatters}
      sorts={sorts}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      defaultPageSize={DEFAULT_PAGE_SIZE}
      defaultOrderBy="-dateCreated"
      rowIdentifier={rowIdentifier}
      onDoubleClick={onDoubleClick}
      defaultFilters={defaultFilters()}
      rowDisabled={isRowDisabled}
      rights={rights}
    />
  );
}

export default TaskSearcher;
